import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, Mail, Lock, Eye, EyeOff, Check, X, Crown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { apiClient } from "@/api/ApiClient";
import { useAuth } from "@/contexts/AuthContext";
import { SocialLoginButtons, hasSocialLogin } from "@/components/SocialLoginButtons";

interface CredentialsSettingsProps {
  user?: {
    id?: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
    emailVerified?: boolean;
  };
  onCredentialsUpdated?: (newEmail?: string) => void;
}

export const CredentialsSettings = ({ user, onCredentialsUpdated }: CredentialsSettingsProps) => {
  const { applySession } = useAuth();
  const [showSetCredentialsModal, setShowSetCredentialsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
    emailPassword: false
  });
  
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [nextResendAvailableAt, setNextResendAvailableAt] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    emailPassword: "",
    newEmail: ""
  });

  // Password match validation
  const isPasswordMatch = formData.newPassword === formData.confirmPassword;
  const showPasswordMismatch = formData.confirmPassword && !isPasswordMatch;
  
  const { toast } = useToast();
  const { t } = useTranslation();

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSetCredentials = async () => {
    if (!formData.email || !formData.password) {
      toast({
        title: t('common.error'),
        description: t('credentials.fill_all_fields'),
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: t('common.error'),
        description: t('credentials.password_min_length'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.setCredentials(formData.email, formData.password);

      if (response.success) {
        toast({
          title: t('common.success'),
          description: t('credentials.set_success'),
        });
        setShowSetCredentialsModal(false);
        setFormData(prev => ({ ...prev, email: "", password: "" }));
        onCredentialsUpdated?.(formData.email);
      } else {
        throw new Error(response.message || "Failed to set credentials");
      }
    } catch (error: any) {
      console.error('Error setting credentials:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('credentials.set_error'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('credentials.fill_all_fields'),
        variant: "destructive",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('credentials.passwords_mismatch'),
        variant: "destructive",
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      toast({
        title: t('common.error'),
        description: t('credentials.new_password_min_length'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.updatePassword(formData.currentPassword, formData.newPassword);

      if (response.success) {
        toast({
          title: t('common.success'),
          description: t('credentials.password_changed'),
        });
        setShowChangePasswordModal(false);
        setFormData(prev => ({ 
          ...prev, 
          currentPassword: "", 
          newPassword: "", 
          confirmPassword: "" 
        }));
      } else {
        throw new Error(response.message || "Failed to change password");
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('credentials.password_change_error'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!formData.emailPassword || !formData.newEmail) {
      toast({
        title: t('common.error'),
        description: t('credentials.fill_all_fields'),
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.newEmail)) {
      toast({
        title: t('common.error'),
        description: t('credentials.invalid_email'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.updateEmail(formData.emailPassword, formData.newEmail);

      if (response.success) {
        toast({
          title: t('common.success'),
          description: t('credentials.email_changed'),
        });
        setShowChangeEmailModal(false);
        setFormData(prev => ({ 
          ...prev, 
          emailPassword: "", 
          newEmail: "" 
        }));
        onCredentialsUpdated?.(formData.newEmail);
      } else {
        throw new Error(response.message || "Failed to change email");
      }
    } catch (error: any) {
      console.error('Error changing email:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('credentials.email_change_error'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      const response = await apiClient.resendEmailVerification();
      if (response?.nextResendAvailableAt) {
        setNextResendAvailableAt(response.nextResendAvailableAt);
      }
      if (response?.success) {
        toast({
          title: t('profile.resend_verification_toast_title'),
          description: t('profile.resend_verification_toast_desc'),
        });
      } else {
        toast({
          title: t('credentials.please_wait'),
          description: response?.message || t('profile.resend_verification_error'),
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      console.error('Error resending verification:', error);
      const message = error instanceof Error ? error.message : undefined;
      toast({
        title: t('common.error'),
        description: message || t('profile.resend_verification_error'),
        variant: "destructive",
      });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const formatCountdown = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const nextResendMs = nextResendAvailableAt ? new Date(nextResendAvailableAt).getTime() : null;
  const isCooldownActive = nextResendMs !== null && Number.isFinite(nextResendMs) && now < nextResendMs;
  const remainingMs = isCooldownActive && nextResendMs !== null ? nextResendMs - now : 0;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Load cooldown window when we open the settings page.
  useEffect(() => {
    const loadAvailability = async () => {
      if (!user?.id || user.emailVerified) return;
      try {
        const res = await apiClient.get('/users/me/verification-resend-available');
        if (res?.nextResendAvailableAt) {
          setNextResendAvailableAt(res.nextResendAvailableAt);
        }
      } catch (e) {
        console.warn('Failed to load verification resend availability:', e);
      }
    };

    loadAvailability();
    // We intentionally don't include `nextResendAvailableAt` to avoid loops.
  }, [user?.id, user?.emailVerified]);

  return (
    <div className="space-y-4">
      {/* Current Email Display - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-full">
                <Mail className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold">{t('credentials.current_email')}</h3>
                <p className="text-sm text-muted-foreground">
                  {user.email}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {user.emailVerified ? (
                    <>
                      <Check className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600 font-medium">{t('credentials.verified')}</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-red-600 font-medium">{t('credentials.not_verified')}</span>
                    </>
                  )}
                </div>
                {user?.email && !user.emailVerified && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 h-8 text-xs"
                    disabled={isResendingVerification || isCooldownActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResendVerification();
                    }}
                  >
                    {isResendingVerification
                      ? t('profile.resend_verification_sending')
                      : isCooldownActive
                        ? t('credentials.resend_in').replace('{{time}}', formatCountdown(remainingMs))
                        : t('profile.resend_verification_link')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Set Credentials - Only show if user doesn't have email */}
      {!user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowSetCredentialsModal(true)}
              className="bg-black hover:bg-black/90 text-white border-black"
            >
              {t('credentials.set')}
            </Button>
          </div>
          {hasSocialLogin() && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-muted-foreground">{t('credentials.save_with_provider')}</p>
              {/* Signed in: the backend links the provider account to this one. */}
              <SocialLoginButtons
                onSession={async (session) => {
                  await applySession(session);
                  onCredentialsUpdated?.(session.user.email ?? undefined);
                }}
              />
            </div>
          )}
        </Card>
      )}

      {/* Change Password - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <Lock className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">{t('credentials.change_password')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('credentials.change_password_desc')}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowChangePasswordModal(true)}
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              {t('credentials.change')}
            </Button>
          </div>
        </Card>
      )}

      {/* Change Email - Only show if user has email */}
      {user?.email && (
        <Card className="p-4 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">{t('credentials.change_email')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('credentials.change_email_desc')}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowChangeEmailModal(true)}
              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
            >
              {t('credentials.change')}
            </Button>
          </div>
        </Card>
      )}

      {/* Set Credentials Modal */}
      <Dialog open={showSetCredentialsModal} onOpenChange={setShowSetCredentialsModal}>
        <DialogContent className="sm:max-w-md" hideCloseButton>
          <DialogHeader className="items-center text-center sm:text-center">
            <DialogTitle className="flex items-center justify-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              {t('credentials.set_credentials')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('credentials.set_credentials_modal_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="set-email">{t('credentials.email')}</Label>
              <Input
                id="set-email"
                type="email"
                placeholder={t('credentials.email_placeholder')}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="set-password">{t('credentials.password')}</Label>
              <div className="relative">
                <Input
                  id="set-password"
                  type={showPasswords.newPassword ? "text" : "password"}
                  placeholder={t('credentials.enter_password')}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {showPasswords.newPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowSetCredentialsModal(false)}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1"
                onClick={handleSetCredentials}
                disabled={isLoading}
              >
                {isLoading ? t('credentials.setting') : t('credentials.set')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-green-600" />
              {t('credentials.change_password')}
            </DialogTitle>
            <DialogDescription>
              {t('credentials.change_password_modal_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">{t('credentials.current_password')}</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPasswords.currentPassword ? "text" : "password"}
                  placeholder={t('credentials.enter_current_password')}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('currentPassword')}
                >
                  {showPasswords.currentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('credentials.new_password')}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPasswords.newPassword ? "text" : "password"}
                  placeholder={t('credentials.enter_new_password')}
                  value={formData.newPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {showPasswords.newPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('credentials.confirm_new_password')}</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  placeholder={t('credentials.confirm_new_password_placeholder')}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={showPasswordMismatch ? "border-red-500 focus:border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                >
                  {showPasswords.confirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {/* Password match indicator */}
              {formData.confirmPassword && (
                <div className="flex items-center gap-2 text-xs">
                  {isPasswordMatch ? (
                    <>
                      <Check className="h-3 w-3 text-green-500" />
                      <span className="text-green-600">{t('credentials.passwords_match')}</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 text-red-500" />
                      <span className="text-red-600">{t('credentials.passwords_no_match')}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowChangePasswordModal(false)}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1"
                onClick={handleChangePassword}
                disabled={isLoading || showPasswordMismatch}
              >
                {isLoading ? t('credentials.changing') : t('credentials.change_password')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Email Modal */}
      <Dialog open={showChangeEmailModal} onOpenChange={setShowChangeEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              {t('credentials.change_email')}
            </DialogTitle>
            <DialogDescription>
              {t('credentials.change_email_modal_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email-password">{t('credentials.current_password')}</Label>
              <div className="relative">
                <Input
                  id="email-password"
                  type={showPasswords.emailPassword ? "text" : "password"}
                  placeholder={t('credentials.enter_current_password')}
                  value={formData.emailPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, emailPassword: e.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility('emailPassword')}
                >
                  {showPasswords.emailPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">{t('credentials.new_email')}</Label>
              <Input
                id="new-email"
                type="email"
                placeholder={t('credentials.new_email_placeholder')}
                value={formData.newEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, newEmail: e.target.value }))}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowChangeEmailModal(false)}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1"
                onClick={handleChangeEmail}
                disabled={isLoading}
              >
                {isLoading ? t('credentials.changing') : t('credentials.change_email')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
